import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import {
  Eye, Printer, CheckCircle, XCircle, RefreshCw,
  ChevronLeft, ChevronRight, ArrowUpDown, Search
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OrdersManagement = ({ viewOrderId, onViewed }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/`).then(res => setSiteSettings(res.data)).catch(() => {});
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/orders/`);
      setOrders(res.data);
    } catch (err) {
      toast.error('Gagal memuat data pesanan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto-open detail when viewOrderId is set (from OverviewTab eye click)
  useEffect(() => {
    if (viewOrderId && orders.length > 0) {
      const order = orders.find(o => o.id === viewOrderId);
      if (order) {
        handleViewDetail(order);
        if (onViewed) onViewed();
      }
    }
  }, [viewOrderId, orders, onViewed]);

  const handleViewDetail = async (order) => {
    try {
      const res = await axios.get(`${API}/orders/${order.id}`);
      setSelectedOrder(res.data);
      setShowDetail(true);
    } catch {
      toast.error('Gagal memuat detail pesanan');
    }
  };

  const handleUpdateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}/status`, { status });
      toast.success('Status berhasil diupdate');
      fetchOrders();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({...selectedOrder, booking_status: status});
      }
    } catch (err) {
      toast.error('Gagal update status');
    }
  };

  const handleConfirmPayment = async (orderId) => {
    try {
      await axios.put(`${API}/orders/${orderId}/confirm-payment`);
      toast.success('Pembayaran berhasil dikonfirmasi');
      setShowDetail(false);
      fetchOrders();
    } catch (err) {
      toast.error('Gagal konfirmasi: ' + (err.response?.data?.detail || err.message));
    }
  };

  const formatRupiah = (num) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(num || 0);
  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const formatDateShort = (d) =>
    d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const statusColor = (s) => {
    switch (s) {
      case 'paid': return 'default';
      case 'pending': return 'secondary';
      case 'failed': return 'destructive';
      case 'cancelled': return 'outline';
      case 'completed': return 'default';
      case 'waiting_confirmation': return 'secondary';
      case 'confirmed': return 'secondary';
      default: return 'secondary';
    }
  };
  const statusLabel = (s) => {
    const m = { pending: 'Pending', paid: 'Lunas', failed: 'Gagal', cancelled: 'Dibatalkan', confirmed: 'Dikonfirmasi', processing: 'Diproses', completed: 'Selesai', waiting_confirmation: 'Menunggu Konfirmasi', dp: 'DP Dibayar' };
    return m[s] || s;
  };
  const payStatusIcon = (s) => {
    if (s === 'paid') return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (s === 'failed') return <XCircle className="h-4 w-4 text-red-500" />;
    return null;
  };

  // Filtering and sorting
  let filtered = [...orders];
  if (statusFilter !== 'all') {
    if (statusFilter === 'paid' || statusFilter === 'pending' || statusFilter === 'failed') {
      filtered = filtered.filter(o => o.payment_status === statusFilter);
    } else {
      filtered = filtered.filter(o => o.booking_status === statusFilter);
    }
  }
  if (searchText) {
    const q = searchText.toLowerCase();
    filtered = filtered.filter(o =>
      o.order_number?.toLowerCase().includes(q) ||
      o.user_id?.toLowerCase().includes(q) ||
      o.package?.package_name?.toLowerCase().includes(q)
    );
  }
  filtered.sort((a, b) => {
    const da = new Date(a.created_at || 0), db = new Date(b.created_at || 0);
    return sortOrder === 'desc' ? db - da : da - db;
  });
  const pageCount = Math.ceil(filtered.length / pageSize);
  const pageOrders = filtered.slice(page * pageSize, (page + 1) * pageSize);

  // Receipt HTML
  const renderReceiptHtml = (orderOverride) => {
    const o = orderOverride || selectedOrder;
    if (!o) return '';
    const paid = o.paid_amount || (o.payment_status === 'paid' ? o.total_amount : (o.payment_status === 'dp' ? o.dp_amount : 0));
    const remaining = o.remaining_amount || (o.payment_status === 'dp' ? Math.max(0, o.total_amount - (o.dp_amount || 0)) : 0);
    const isDp = o.payment_status === 'dp' || (o.dp_amount > 0 && o.remaining_amount > 0);
    const isPaid = o.payment_status === 'paid';
    const s = siteSettings || {};
    const alamat = s.address || '';
    const kota = s.city || 'Tangerang';
    const telp1 = s.contact_phone_1 || '';
    const telp2 = s.contact_phone_2 || '';
    const email = s.email || 'admin@layananumroh.com';
    const web = s.site_name ? s.site_name + '.com' : 'layananumroh.com';
    const bank = (s.bank_accounts && s.bank_accounts[0]) ? s.bank_accounts[0] : null;
    const jatuhTempo = o.package?.departure_date ? new Date(new Date(o.package.departure_date).getTime() - 30*24*60*60*1000).toLocaleDateString('id-ID', {day:'numeric',month:'long',year:'numeric'}) : '-';
    const today = new Date().toLocaleDateString('id-ID', {day:'numeric',month:'long',year:'numeric'});
    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Kuitansi - ${o.order_number}</title>
<style>
@page { margin: 12mm 15mm; }
* { box-sizing: border-box; }
body { font-family: 'Segoe UI', Arial, sans-serif; color: #333; line-height: 1.4; margin: 0; padding: 0; }
.header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #C9A84C; padding-bottom: 14px; margin-bottom: 12px; }
.header-left { display: flex; gap: 12px; align-items: center; }
.header-left img { height: 55px; width: auto; }
.header-left .brand { font-size: 18px; font-weight: 700; color: #222; margin: 0; line-height: 1.2; }
.header-left .tagline { font-size: 11px; color: #C9A84C; margin: 0; }
.header-left .contact { font-size: 10px; color: #666; margin: 3px 0 0; line-height: 1.3; }
.header-right { text-align: right; }
.header-right h1 { font-size: 28px; font-weight: 900; color: #111; margin: 0; letter-spacing: 2px; }
.header-right p { font-size: 16px; font-weight: 700; color: #C9A84C; margin: 0; letter-spacing: 1px; border-bottom: 2px solid #C9A84C; padding-bottom: 2px; }
.info-row { display: flex; justify-content: space-between; font-size: 11px; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px dashed #ddd; }
.info-row div { flex: 1; }
.info-row .label { color: #999; font-size: 9px; text-transform: uppercase; margin: 0 0 2px; }
.info-row .value { font-weight: 600; color: #222; margin: 0; }
table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
th { background: #1E293B; color: #fff; font-size: 11px; padding: 6px 8px; text-align: left; }
th:last-child { text-align: right; }
td { padding: 5px 8px; font-size: 11px; border-bottom: 1px solid #e5e7eb; }
td:last-child { text-align: right; }
.tr-dp td { color: #C9A84C; font-weight: 600; }
.tr-total td { font-weight: 700; font-size: 12px; background: #f8f5f0; }
.status-box { margin-top: 0px; }
.status-grid { display: flex; gap: 24px; margin-top: 8px; }
.status-card { flex: 1; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; background: #fafafa; }
.status-card .lbl { font-size: 9px; text-transform: uppercase; color: #999; margin: 0 0 4px; }
.status-card .val { font-size: 20px; font-weight: 700; color: #1E293B; margin: 0; }
.status-card .val.gold { color: #C9A84C; }
.status-card .sub { font-size: 9px; color: #aaa; margin: 4px 0 0; }
.badge-dp { display: inline-block; background: #C9A84C; color: #fff; font-size: 10px; font-weight: 600; padding: 2px 10px; border-radius: 4px; margin-top: 4px; }
.metode { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.metode .rek { font-size: 10px; color: #555; }
.metode .rek strong { color: #1E293B; }
.terms { margin-top: 12px; padding: 10px; border: 1px solid #C9A84C; border-radius: 6px; font-size: 9px; color: #444; }
.terms h5 { margin: 0 0 4px; color: #1E293B; font-size: 10px; }
.terms p { margin: 2px 0; }
.terms ul { margin: 2px 0; padding-left: 14px; }
.terms li { margin: 1px 0; }
.sign-row { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 16px; }
.sign-row div { text-align: center; font-size: 10px; }
.sign-row .stamp { height: 65px; width: 65px; border: 2px solid #C9A84C; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 7px; color: #C9A84C; line-height: 1.2; margin-bottom: 4px; }
.footer-bar { margin-top: 16px; padding: 8px 0; border-top: 1px solid #e5e7eb; display: flex; justify-content: center; gap: 20px; flex-wrap: wrap; font-size: 8px; color: #888; }
.footer-bar span { display: inline-flex; align-items: center; gap: 3px; }
.footer-bar .icon { color: #C9A84C; font-size: 10px; }
.jamaah-box { margin: 10px 0; }
.jamaah-title { font-size: 10px; font-weight: 700; color: #1E293B; margin: 0 0 4px; letter-spacing: .3px; }
.jamaah-table { width: 100%; border-collapse: collapse; font-size: 9.5px; }
.jamaah-table th { background: #F1F5F9; color: #1E293B; text-align: left; padding: 4px 6px; border: 1px solid #E2E8F0; font-weight: 600; }
.jamaah-table td { padding: 4px 6px; border: 1px solid #E2E8F0; color: #334155; }
@media print { body { font-size: 11px; } .no-print { display: none; } }
</style></head><body>
<div class="header">
  <div class="header-left">
    <img src="${window.location.origin}/logo.png" alt="Logo" style="height:55px" onerror="this.style.display='none'" />
    <div>
      <p class="brand">layanan.umroh</p>
      <p class="tagline">✦ ORGANIZER</p>
      <p class="contact">${alamat}<br>Telp: ${telp1} | ${telp2}<br>Email: ${email}<br>${web}</p>
    </div>
  </div>
  <div class="header-right">
    <h1>KUITANSI</h1>
    <p>PEMBAYARAN</p>
  </div>
</div>
<div class="info-row">
  <div>
    <p class="label">Kepada Yth.</p>
    <p class="value">${(o.jamaahs && o.jamaahs[0]?.full_name) || '-'}</p>
    <p style="font-size:10px;color:#666;margin:0">Telp: ${(o.jamaahs && o.jamaahs[0]?.phone) || '-'}<br>Email: ${(o.jamaahs && o.jamaahs[0]?.email) || '-'}</p>
  </div>
  <div>
    <p class="label">Paket &amp; Keberangkatan</p>
    <p class="value">${o.package?.package_name || '-'}</p>
    <p style="font-size:10px;color:#666;margin:0">${o.package?.departure_date ? 'Berangkat: ' + new Date(o.package.departure_date).toLocaleDateString('id-ID', {day:'numeric',month:'long',year:'numeric'}) : ''}</p>
  </div>
  <div style="text-align:right">
    <p class="label">Invoice</p>
    <p class="value">INV/${o.order_number}</p>
    <p style="font-size:10px;color:#666;margin:0">Tanggal: ${formatDateShort(o.created_at)}</p>
  </div>
</div>
${(o.jamaahs && o.jamaahs.length > 0) ? `
<div class="jamaah-box">
  <p class="jamaah-title">RINCIAN JAMAAH (${o.jamaahs.length} orang)</p>
  <table class="jamaah-table">
    <tr><th style="width:22px">No</th><th>Nama Lengkap</th><th>NIK</th><th>No. Paspor</th><th>L/P</th><th>Tgl Lahir</th><th>Telepon</th></tr>
    ${o.jamaahs.map((j, i) => `<tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${j.full_name || '-'}</td>
      <td>${j.nik || '-'}</td>
      <td>${j.passport_number || '-'}</td>
      <td style="text-align:center">${j.gender === 'male' ? 'L' : (j.gender === 'female' ? 'P' : '-')}</td>
      <td>${j.date_of_birth ? formatDateShort(j.date_of_birth) : '-'}</td>
      <td>${j.phone || '-'}</td>
    </tr>`).join('')}
  </table>
</div>` : ''}
<table>
<tr><th>URAIAN</th><th style="text-align:right">NOMINAL</th></tr>
<tr><td>Total Biaya Paket</td><td>${formatRupiah(o.subtotal)}</td></tr>
<tr><td>DP (Uang Muka) - Telah Dibayar</td><td>${formatRupiah(paid)}</td></tr>
<tr class="tr-dp"><td>Sisa Pelunasan ${isDp ? '(Belum Dibayar)' : ''}</td><td>${formatRupiah(remaining)}</td></tr>
<tr class="tr-total"><td>TOTAL YANG AKAN DIBAYAR</td><td>${formatRupiah(o.total_amount)}</td></tr>
</table>
<div class="status-box">
  <div class="status-grid">
    <div class="status-card">
      <p class="lbl">Status Pembayaran</p>
      <p class="val gold">${isPaid ? 'LUNAS' : isDp ? 'DP (BELUM LUNAS)' : 'BELUM LUNAS'}</p>
      <div class="badge-dp">${isPaid ? 'LUNAS' : isDp ? 'DP (BELUM LUNAS)' : 'PENDING'}</div>
      <p class="sub">Pembayaran DP dianggap sebagai persetujuan terhadap ketentuan Program Umrah layanan.umroh.</p>
    </div>
    <div class="status-card">
      <p class="lbl">Sisa Yang Harus Dilunasi</p>
      <p class="val gold">${formatRupiah(remaining)}</p>
      <p class="sub">Jatuh Tempo: Paling lambat H-30 sebelum tanggal keberangkatan</p>
    </div>
    <div class="status-card">
      <p class="lbl">Metode Pembayaran</p>
      <div class="metode">
        <div>
          <p class="rek"><strong>${bank ? bank.bank_name : 'BCA Syariah'}</strong><br>${bank ? bank.account_name : 'PT Layanan Umrah Haji Indonesia'}<br><strong>${bank ? bank.account_number : '0095788154'}</strong></p>
        </div>
      </div>
    </div>
  </div>
</div>
<div class="terms">
  <h5>KETENTUAN PROGRAM LAYANAN.UMROH</h5>
  <div style="display:flex;gap:20px;">
    <div style="flex:1">
      <p><strong>PEMBAYARAN:</strong></p>
      <ul>
        <li>Pelunasan maksimal H-30 sebelum tanggal keberangkatan.</li>
        <li>Pembatalan di atas H-30 dikenakan biaya administrasi Rp1.000.000/jamaah (dipotong dari DP).</li>
        <li>Pembatalan H-30 atau kurang tidak diperbolehkan.</li>
      </ul>
    </div>
    <div style="flex:1">
      <p><strong>PERUBAHAN JADWAL:</strong></p>
      <ul>
        <li>Perubahan jadwal di atas H-30 dikenakan biaya Rp500.000.</li>
        <li>Perubahan jadwal H-30 atau kurang tidak diperbolehkan.</li>
      </ul>
    </div>
  </div>
</div>
<div class="sign-row">
  <div>
    <p style="font-size:10px;font-weight:600;margin:0 0 4px">${kota}, ${today}</p>
    <div style="margin-top:4px;">
      <div><img src="/ttd.png" style="width:100px;height:auto" alt="ttd" /></div>
      <p style="font-size:10px;font-weight:600;margin:4px 0 0">( Admin )</p>
    </div>
  </div>
  <div>
    <p style="font-size:9px;color:#999;margin:0">Amanah Melayani Ibadah Nyaman</p>
  </div>
</div>
<div class="footer-bar">
  <span><span class="icon">✓</span> Amanah Melayani</span>
  <span><span class="icon">✈</span> Tiket Pasti</span>
  <span><span class="icon">🏨</span> Hotel Nyaman</span>
  <span><span class="icon">🚌</span> Transportasi Terpercaya</span>
  <span><span class="icon">♡</span> Pelayanan Terbaik</span>
</div>
</body></html>`;};

  const handlePrintReceipt = async (orderOverride) => {
    const o = orderOverride || selectedOrder;
    if (!o) return;
    // Fetch full order detail if not loaded (table row only has partial data)
    let fullOrder = o;
    if (!o.jamaahs && !o.package) {
      try {
        const res = await axios.get(`${API}/orders/${o.id}`);
        fullOrder = res.data;
      } catch { /* fallback to partial data */ }
    }
    const w = window.open();
    if (!w) { toast.error('Aktifkan pop-up untuk mencetak'); return; }
    w.document.write(renderReceiptHtml(fullOrder));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 500);
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Memuat data...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Orders Management</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9 rounded-xl w-60" placeholder="Cari order number..." value={searchText} onChange={e => { setSearchText(e.target.value); setPage(0); }} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { label: 'All', key: 'all' },
          { label: 'Lunas', key: 'paid' },
          { label: 'Pending', key: 'pending' },
          { label: 'Gagal', key: 'failed' },
          { label: 'Menunggu Konfirmasi', key: 'waiting_confirmation' },
          { label: 'Dikonfirmasi', key: 'confirmed' },
          { label: 'Diproses', key: 'processing' },
        ].map(f => (
          <Badge
            key={f.key}
            variant={statusFilter === f.key ? 'default' : 'outline'}
            className="cursor-pointer text-sm py-1.5 px-3"
            onClick={() => { setStatusFilter(f.key); setPage(0); }}
          >
            {f.label}
          </Badge>
        ))}
        <Button variant="outline" size="sm" className="ml-auto" onClick={() => setSortOrder(s => s === 'desc' ? 'asc' : 'desc')}>
          <ArrowUpDown className="h-3 w-3 mr-1" /> {sortOrder === 'desc' ? 'Terbaru' : 'Terlama'}
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle>Daftar Pesanan ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {pageOrders.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Belum ada data pesanan</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 font-semibold text-sm">Order #</th>
                    <th className="text-left p-3 font-semibold text-sm">Tanggal</th>
                    <th className="text-left p-3 font-semibold text-sm">Peserta</th>
                    <th className="text-left p-3 font-semibold text-sm">Total</th>
                    <th className="text-left p-3 font-semibold text-sm">Status Bayar</th>
                    <th className="text-left p-3 font-semibold text-sm">Status Pesanan</th>
                    <th className="text-right p-3 font-semibold text-sm">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {pageOrders.map((o) => (
                    <tr key={o.id} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => handleViewDetail(o)}>
                      <td className="p-3 text-sm font-medium">{o.order_number}</td>
                      <td className="p-3 text-sm text-muted-foreground">{formatDate(o.created_at)}</td>
                      <td className="p-3 text-sm">{o.total_pilgrims} org</td>
                      <td className="p-3 text-sm">{formatRupiah(o.total_amount)}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          {payStatusIcon(o.payment_status)}
                          <Badge variant={statusColor(o.payment_status)}>{statusLabel(o.payment_status)}</Badge>
                        </div>
                      </td>
                      <td className="p-3">
                        <Badge variant={statusColor(o.booking_status)}>{statusLabel(o.booking_status)}</Badge>
                      </td>
                      <td className="p-3 text-right">
                        {(o.payment_status === 'paid' || o.payment_status === 'dp') && (
                        <Button variant="outline" size="sm" onClick={() => handlePrintReceipt(o)}>
                          <Printer className="h-3 w-3 mr-1" />
                        </Button>
                      )}
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewDetail(o); }}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {pageCount > 1 && (
                <div className="flex items-center justify-center gap-2 mt-4">
                  <Button variant="outline" size="sm" disabled={page <= 0} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground px-2">{page + 1} / {pageCount}</span>
                  <Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detail Pesanan</DialogTitle>
            <DialogDescription>
              Informasi lengkap untuk follow up admin
            </DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Order Number</Label>
                  <p className="font-medium">{selectedOrder.order_number}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Tanggal</Label>
                  <p className="font-medium">{formatDate(selectedOrder.created_at)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Total Pembayaran</Label>
                  <p className="font-bold text-lg">{formatRupiah(selectedOrder.total_amount)}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Jumlah Peserta</Label>
                  <p className="font-medium">{selectedOrder.total_pilgrims} orang</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status Pembayaran</Label>
                  <div className="flex items-center gap-1">
                    {payStatusIcon(selectedOrder.payment_status)}
                    <Badge variant={statusColor(selectedOrder.payment_status)}>{statusLabel(selectedOrder.payment_status)}</Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status Pesanan</Label>
                  <Badge variant={statusColor(selectedOrder.booking_status)}>{statusLabel(selectedOrder.booking_status)}</Badge>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Rincian Biaya</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-muted-foreground">Subtotal:</span><span className="text-right">{formatRupiah(selectedOrder.subtotal)}</span>
                  <span className="text-muted-foreground">Pajak:</span><span className="text-right">{formatRupiah(selectedOrder.tax)}</span>
                  {selectedOrder.discount > 0 && (<><span className="text-muted-foreground">Diskon:</span><span className="text-right">-{formatRupiah(selectedOrder.discount)}</span></>)}
                  <span className="text-muted-foreground font-semibold border-t pt-1">Total:</span><span className="text-right font-bold border-t pt-1">{formatRupiah(selectedOrder.total_amount)}</span>
                  {selectedOrder.payment_status === 'dp' && (<>
                    <span className="text-muted-foreground text-blue-600">DP Dibayar:</span><span className="text-right text-blue-600">-{formatRupiah(selectedOrder.dp_amount)}</span>
                    <span className="text-muted-foreground font-semibold">Sisa Pelunasan:</span><span className="text-right font-semibold">{formatRupiah(selectedOrder.remaining_amount)}</span>
                  </>)}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Paket</h4>
                {selectedOrder.package ? (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-muted-foreground">Nama Paket:</span><span>{selectedOrder.package.package_name}</span>
                    <span className="text-muted-foreground">Durasi:</span><span>{selectedOrder.package.duration_days} hari</span>
                  </div>
                ) : <p className="text-muted-foreground text-sm">Data paket tidak tersedia</p>}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Jamaah / Peserta</h4>
                {selectedOrder.jamaahs && selectedOrder.jamaahs.length > 0 ? (
                  <div className="space-y-3">
                    {selectedOrder.jamaahs.map((j, i) => (
                      <div key={i} className="p-3 border rounded-lg text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium capitalize">{j.full_name}</p>
                            <p className="text-muted-foreground">NIK: {j.nik || '-'} | Paspor: {j.passport_number || '-'}</p>
                            <p className="text-muted-foreground">{j.gender === 'male' ? 'Laki-laki' : 'Perempuan'} | Tgl Lahir: {j.date_of_birth ? new Date(j.date_of_birth).toLocaleDateString('id-ID') : '-'}</p>
                          </div>
                        </div>
                        <div className="mt-2 text-muted-foreground">
                          <p>📞 {j.phone || '-'} | ✉️ {j.email || '-'}</p>
                          {j.address && <p>📍 {j.address}</p>}
                          {j.emergency_contact_name && <p>🚨 {j.emergency_contact_name} ({j.emergency_contact_phone})</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-muted-foreground text-sm">Belum ada data jamaah</p>}
              </div>

              {/* Payment Confirmation Details */}
              {(selectedOrder.transfer_date || selectedOrder.transfer_bank_name || selectedOrder.transfer_account_holder) && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Konfirmasi Pembayaran</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {selectedOrder.transfer_date && (<><span className="text-muted-foreground">Tanggal Transfer:</span><span>{selectedOrder.transfer_date}</span></>)}
                    {selectedOrder.transfer_bank_name && (<><span className="text-muted-foreground">Bank:</span><span>{selectedOrder.transfer_bank_name}</span></>)}
                    {selectedOrder.transfer_account_holder && (<><span className="text-muted-foreground">Atas Nama:</span><span>{selectedOrder.transfer_account_holder}</span></>)}
                  </div>
                </div>
              )}

              {selectedOrder.payment_proof && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Bukti Pembayaran</h4>
                  {selectedOrder.payment_proof.startsWith('data:') ? (
                    <div className="rounded-lg overflow-hidden border">
                      {selectedOrder.payment_proof.includes('image') ? (
                        <img src={selectedOrder.payment_proof} alt="Bukti transfer" className="max-w-full h-auto max-h-48 object-contain" />
                      ) : (
                        <a href={selectedOrder.payment_proof} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm p-4 block">
                          📄 Lihat PDF bukti pembayaran
                        </a>
                      )}
                    </div>
                  ) : (
                    <a href={selectedOrder.payment_proof} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-sm">
                      Lihat bukti transfer
                    </a>
                  )}
                </div>
              )}

              <DialogFooter className="flex justify-between">
                <div className="flex gap-2 flex-wrap">
                  {['waiting_confirmation', 'pending'].includes(selectedOrder.payment_status) && (
                    <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleConfirmPayment(selectedOrder.id)}>
                      <CheckCircle className="h-3 w-3 mr-1" /> {selectedOrder.dp_amount > 0 ? 'Konfirmasi DP' : 'Konfirmasi Pembayaran'}
                    </Button>
                  )}

                  {selectedOrder.payment_status === 'dp' && (
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={async () => {
                      try {
                        await axios.put(`${API}/orders/${selectedOrder.id}/confirm-balance`);
                        toast.success('Pelunasan dikonfirmasi, order lunas');
                        setSelectedOrder(prev => ({ ...prev, payment_status: 'paid', remaining_amount: 0, paid_amount: prev.total_amount }));
                      } catch (err) {
                        toast.error('Gagal: ' + (err.response?.data?.detail || err.message));
                      }
                    }}>
                      <CheckCircle className="h-3 w-3 mr-1" /> Konfirmasi Pelunasan
                    </Button>
                  )}
                
                  {(selectedOrder.payment_status === 'paid' || selectedOrder.payment_status === 'dp') && (
                    <Button variant="outline" size="sm" onClick={() => handlePrintReceipt(selectedOrder)}>
                      <Printer className="h-3 w-3 mr-1" /> Cetak Kuitansi
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersManagement;
